import { ErrorMessage, Field, Form, Formik } from "formik";
import { ReactElement, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import * as Yup from "yup";
import ic_default from "../../../assets/icons/ic_user_default.svg";
import { ButtonCustom } from "../../../components/custom/button-custom";
import { showToast } from "../../../components/custom/custom-toast";
import DateCustomInput from "../../../components/custom/date-input-custom";
import ImageCustom from "../../../components/custom/image-custom";
import { LoadingCustom } from "../../../components/custom/loading-custom";
import IconCamera from "../../../components/icons/icon-camera";
import { IconEllipse } from "../../../components/icons/icon-group";
import { GENDER } from "../../../constants/status";
import { useFetchData } from "../../../hooks/useFetch";
import useUpload from "../../../hooks/useUpload";
import { EmployeeResponse } from "../../../models/response/employee";
import { MODULE_ROUTE } from "../../../routers/module-router";
import { API_ADMIN_ROUTER, API_UPLOAD_ROUTER } from "../../../services/api-config";
import CookieUtils from "../../../utils/cookie-utils";
import { getTodayDate } from "../../../utils/time-utils";

export const ProfilePage = () => {
  const navigate = useNavigate();
  const [profileData, setProfileData] = useState<EmployeeResponse>(new EmployeeResponse());
  const [imageDisplay, setImageDisplay] = useState("");
  const [imageUpload, setImageUpload] = useState<FileList | null>(null);
  const [isDataLoaded, setIsDataLoaded] = useState(false);
  const [isImageLocal, setIsImageLocal] = useState(false);
  const [employeeRequest, setEmployeeRequest] = useState(new EmployeeResponse());

  const { request: fetchDataProfile, baseResponse: responsesProfile } = useFetchData.Get<EmployeeResponse>();
  const {
    request: fetchDataUpdateProfile,
    baseResponse: responsesUpdateProfile,
    loading: loadingSave,
  } = useFetchData.Post();
  const { uploadFiles: onUploadFile, baseResponse: responseUpload } = useUpload<any>(API_UPLOAD_ROUTER.API_UPLOAD);

  useEffect(() => {
    fetchDataProfile({
      endPoint: API_ADMIN_ROUTER.API_DETAIL_ADMIN(CookieUtils.getCurrentUser()._id ?? ""),
    });
  }, []);

  useEffect(() => {
    if (!responsesProfile) {
      return;
    }
    if (responsesProfile.status === 200 && responsesProfile.data) {
      setProfileData(responsesProfile.data);
      setIsImageLocal(responsesProfile.data.avatar === "");
      setImageDisplay(responsesProfile.data.avatar);
    }
  }, [responsesProfile]);

  useEffect(() => {
    if (!responseUpload) return;
    if (responseUpload.status === 200 && responseUpload.data.length > 0) {
      fetchDataUpdateProfile({
        endPoint: API_ADMIN_ROUTER.API_UPDATE_PROFILE_ADMIN(),
        data: {
          name: employeeRequest.name,
          birthday: employeeRequest.birthday,
          joining_date: employeeRequest.joining_date,
          gender: employeeRequest.gender,
          avatar: responseUpload.data[0],
          phone: employeeRequest.phone,
          address: employeeRequest.address,
        },
      });
    } else {
      showToast({ message: "Có lỗi xảy ra vui lòng thử lại sau", type: "warning" });
    }
  }, [responseUpload]);

  useEffect(() => {
    if (!responsesUpdateProfile) return;
    if (responsesUpdateProfile.status === 200) {
      showToast({ message: "Chỉnh sửa thành công!", type: "success" });
    } else {
      showToast({ message: responsesUpdateProfile.message, type: "warning" });
    }
  }, [responsesUpdateProfile]);

  useEffect(() => {
    if (profileData._id !== "") {
      setIsDataLoaded(true); // Mark data as fully loaded when both profile and branch data are available
    }
  }, [profileData]);

  const handleUpdateProfile = (values: EmployeeResponse) => {
    if (imageUpload) {
      onUploadFile(imageUpload);
      setEmployeeRequest(values);
    } else {
      fetchDataUpdateProfile({
        endPoint: API_ADMIN_ROUTER.API_UPDATE_PROFILE_ADMIN(),
        data: {
          name: values.name,
          birthday: values.birthday,
          joining_date: values.joining_date,
          gender: values.gender,
          phone: values.phone,
          address: values.address,
        },
      });
    }
  };

  const handleImageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files; // Lấy tệp hình ảnh
    if (files && files.length > 0) {
      setIsImageLocal(true);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImageDisplay(reader.result as string); // Cập nhật URL hình ảnh
      };
      reader.readAsDataURL(files[0]); // Đọc tệp dưới dạng URL
      setImageUpload(files);
    }
  };

  const SubmittedForm = Yup.object().shape({
    name: Yup.string()
      .min(2, "Độ dài tối thiểu 2 ký tự")
      .max(50, "Độ dài tối đa 50 ký tự")
      .required("Độ dài tối thiểu 2 ký tự"),
    phone: Yup.string()
      .matches(/^0/, "Số điện thoại chưa đúng định dang, số đầu tiên phải là số 0")
      .min(10, "Số điện thoại chưa đúng 10 hoặc 11 số"),
    id_card_number: Yup.string().min(9, "Độ dài tối thiểu 9 ký tự").max(12, "Độ dài tối đa 12 ký tự"),
  });

  interface RenderFieldProps {
    label: string;
    name: string;
    field: ReactElement<any, any>;
  }

  function renderField({ label, name, field }: RenderFieldProps) {
    return (
      <>
        <label htmlFor={name}>{label}</label>
        {field}
        <ErrorMessage name={name} component="div" className="mt-1 text-xs text-red-500" />
      </>
    );
  }

  if (!isDataLoaded) {
    return <LoadingCustom />;
  }

  return (
    <div className="self-center w-full p-4 mx-auto bg-white shadow-2xl rounded-xl h-fit dark:bg-gray-800/40">
      <div className="flex items-center justify-center w-full h-44">
        <div className="relative">
          <ImageCustom
            url={`${imageDisplay || ic_default}`}
            className="w-[150px] h-[150px] rounded-full object-cover"
            isLocal={isImageLocal}
            data-testid="image-profile"
          />
          <IconEllipse className="absolute inset-0 flex items-center justify-center w-[150px] h-[150px]" />
          <div className="absolute bottom-0 right-0 p-2 border border-white rounded-full cursor-pointer bg-black/50">
            <input
              type="file"
              name="profile"
              accept="image/*"
              className="absolute inset-0 opacity-0 cursor-pointer"
              onChange={handleImageChange}
              data-testid="input-profile-file"
              title=""
            />
            <IconCamera duotone={false} fill color="white" className="cursor-pointer" />
          </div>
        </div>
      </div>
      <Formik initialValues={profileData} validationSchema={SubmittedForm} onSubmit={handleUpdateProfile}>
        {({ values, submitForm, setFieldValue }) => (
          <Form>
            <div className="flex justify-center w-full gap-4 lg:flex-row md:flex-col sm:flex-col xs:flex-col">
              <div className="w-full mt-6">
                {renderField({
                  label: "Họ và tên",
                  name: "name",
                  field: (
                    <Field name="name" type="text" placeholder="Họ và tên" className="form-input" value={values.name} />
                  ),
                })}
              </div>
              <div className="w-full mt-6">
                <label htmlFor="gender">Giới tính</label>
                <div className="flex items-center space-x-4">
                  <label className="flex items-center mt-2">
                    <Field
                      type="radio"
                      name="gender"
                      value={GENDER.MALE}
                      className="form-radio"
                      onChange={() => setFieldValue("gender", GENDER.MALE)}
                    />
                    <span className="ml-2">Nam</span>
                  </label>
                  <label className="flex items-center mt-2">
                    <Field
                      type="radio"
                      name="gender"
                      value={GENDER.FEMALE}
                      className="form-radio"
                      onChange={() => setFieldValue("gender", GENDER.FEMALE)}
                    />
                    <span className="ml-2">Nữ</span>
                  </label>
                </div>
              </div>
            </div>
            <div className="flex justify-center w-full gap-4 lg:flex-row md:flex-col sm:flex-col xs:flex-col">
              <div className="w-full mt-6">
                {renderField({
                  label: "Ngày sinh",
                  name: "birthday",
                  field: (
                    <Field name="birthday">
                      {() => (
                        <DateCustomInput
                          maxDate={getTodayDate()}
                          label="Ngày sinh"
                          value={values.birthday}
                          onChange={(date: string) => {
                            setFieldValue("birthday", date);
                          }}
                        />
                      )}
                    </Field>
                  ),
                })}
              </div>
              <div className="w-full mt-6">
                {renderField({
                  label: "Ngày vào làm việc",
                  name: "joining_date",
                  field: (
                    <Field name="joining_date">
                      {() => (
                        <DateCustomInput
                          disabled
                          label="Ngày vào làm việc"
                          value={values.joining_date}
                          onChange={(date: string) => {
                            setFieldValue("joining_date", date);
                          }}
                        />
                      )}
                    </Field>
                  ),
                })}
              </div>
            </div>
            <div className="flex justify-center w-full gap-4 lg:flex-row md:flex-col sm:flex-col xs:flex-col">
              <div className="w-full mt-6">
                {renderField({
                  label: "Số điện thoại",
                  name: "phone",
                  field: (
                    <Field
                      name="phone"
                      maxLength={11}
                      type="text"
                      placeholder="Số điện thoại"
                      className="form-input"
                      value={values.phone}
                    />
                  ),
                })}
              </div>
            </div>

            <div className="flex justify-center w-full gap-4 lg:flex-row md:flex-col sm:flex-col xs:flex-col">
              <div className="w-full mt-6">
                {renderField({
                  label: "Địa chỉ",
                  name: "address",
                  field: (
                    <Field
                      name="address"
                      type="text"
                      maxLength={255}
                      placeholder="Địa chỉ"
                      className="form-input"
                      value={values.address}
                    />
                  ),
                })}
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <ButtonCustom
                type="button"
                text="Đổi mật khẩu"
                className="px-4 py-2 font-bold text-[#E96012] hover:text-white rounded-md bg-[#FEEED6] hover:bg-[#e96012]"
                onClick={() => {
                  navigate(MODULE_ROUTE.CHANGE_PASSWORD);
                }}
              />
              <button
                type="button"
                className="btn bg-[#E3ECF5] text-[#1462B0] hover:bg-[#1462B0] hover:text-white uppercase font-bold"
                onClick={submitForm}
                disabled={loadingSave}
                data-testid="save-profile-button"
              >
                {loadingSave && (
                  <span className="inline-block w-5 h-5 align-middle border-2 border-white rounded-full animate-spin border-l-transparent ltr:mr-4 rtl:ml-4"></span>
                )}
                Lưu lại
              </button>
            </div>
          </Form>
        )}
      </Formik>
    </div>
  );
};
